import { useContext } from 'react';
import { useTranslation } from 'react-i18next';

import ModuleContext from '../../../_contexts/ModuleContext';
import AccountContext from '../../../../../contexts/AccountContext';
import useModuleAccess from '../../../../../hooks/useModuleAccess';
import { FEATURE_ACCESS } from '../../../../../core/access/module-access';
import { useSlideSetAnalytics } from '../../../../../hooks/useSlideSetAnalytics';
import useSlideSetApps from './hooks/useSlideSetApps';
import useWordListApps from './hooks/useWordListApps';
import useOtherApps from './hooks/useOtherApps';
import { ANALYTICS_EVENT_SUBLOCATIONS } from '../../../../../constants/analyticsEvents';
import { buildDoceoUrl } from '../../../../../utils/slideSet';
import { MaterialIcon } from '../../../components/MaterialIcon';

import { type ButtonProps } from '@pelckmans/elementis/components/button';
import ButtonDropdown from '@pelckmans/elementis/components/button-dropdown';
import DropdownMenuGroup from '@pelckmans/elementis/components/dropdown-menu/group';
import DigibookIcon from '@pelckmans/elementis/icons/digibook';
import MaterialSiteIcon from '@pelckmans/elementis/icons/material-site';
import MaterialWordListIcon from '@pelckmans/elementis/icons/material-wordlist';

export interface MobileDigibookDropdownItem {
  id: string;
  text: string;
  href: string;
  subText?: string;
  disabled?: boolean;
  tooltip?: string;
  onClick?: () => void;
}

interface Props extends Pick<ButtonProps, 'size'> {
  slideSets?: ReturnType<typeof useSlideSetApps>['slideSets'];
  digibooks?: MobileDigibookDropdownItem[];
}

export default function MobileAppsDropdownButton({ size = 'md', slideSets = [], digibooks = [] }: Props) {
  const { t } = useTranslation();
  const { module } = useContext(ModuleContext);
  const {
    account: { provider },
  } = useContext(AccountContext);

  const { captureOpened: captureSlideSetOpened } = useSlideSetAnalytics();
  const { appsAccess } = useModuleAccess(module);
  const appsAllowed = appsAccess === FEATURE_ACCESS.ALLOWED;
  const { wordLists, openWordlist, getDisabledTooltipKey } = useWordListApps();
  const { otherApps, getAppActionProps } = useOtherApps();

  if (!module) return null;
  if (slideSets.length === 0 && wordLists.length === 0 && otherApps.length === 0 && digibooks.length === 0) return null;

  const subLocationId = module.isExerciseModule
    ? ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_EXERCISE_MODULE_MATERIAL
    : ANALYTICS_EVENT_SUBLOCATIONS.PORTAAL_MATERIAL;

  const slideSetTooltip = !appsAllowed ? t('PAGES.TOC_LEVEL.APPS.SLIDE_SET.NO_LICENSE_TOOLTIP') : undefined;

  return (
    <ButtonDropdown
      title={t('PAGES.TOC_LEVEL.APPS.MOBILE.TITLE')}
      buttonProps={{
        prominence: 'secondary',
        startIcon: <MaterialSiteIcon data-testid="mobile-apps-icon" />,
      }}
      size={size}
      disableBackgroundScroll
      emphasized
    >
      {slideSets.length > 0 && (
        <DropdownMenuGroup title={t('PAGES.TOC_LEVEL.APPS.MOBILE.SLIDE_SETS_GROUP')}>
          {slideSets.map(slideSet => {
            const disabledProps = !appsAllowed ? ({ disabled: true, tooltip: slideSetTooltip } as const) : {};

            return (
              <ButtonDropdown.Item
                as="link"
                key={`${slideSet.id}-${slideSet.versionId}`}
                value={slideSet.id}
                text={slideSet.name}
                href={buildDoceoUrl(slideSet, provider)}
                target="_blank"
                rel="noopener noreferrer"
                textIcon={<MaterialIcon materialType="slide-set" isUserSlideSet={Boolean(slideSet.userId)} />}
                onClick={() => captureSlideSetOpened(slideSet.id, subLocationId)}
                {...disabledProps}
              />
            );
          })}
        </DropdownMenuGroup>
      )}
      {digibooks.length > 0 && (
        <DropdownMenuGroup title={t('PAGES.TOC_LEVEL.APPS.MOBILE.DIGIBOOKS_GROUP')}>
          {digibooks.map(digibook => {
            const disabledProps = digibook.disabled
              ? ({ disabled: true, tooltip: digibook.tooltip || '' } as const)
              : {};

            return (
              <ButtonDropdown.Item
                as="link"
                key={digibook.id}
                value={digibook.id}
                text={digibook.text}
                subText={digibook.subText}
                href={digibook.href}
                target="_blank"
                rel="noopener noreferrer"
                textIcon={<DigibookIcon data-testid={`mobile-digibook-icon-${digibook.id}`} />}
                onClick={digibook.onClick}
                {...disabledProps}
              />
            );
          })}
        </DropdownMenuGroup>
      )}
      {wordLists.length > 0 && (
        <DropdownMenuGroup title={t('PAGES.TOC_LEVEL.APPS.MOBILE.WORD_LISTS_GROUP')}>
          {wordLists.map(wordList => {
            const disabledTooltipKey = getDisabledTooltipKey(wordList);
            const disabledProps = disabledTooltipKey
              ? ({ disabled: true, tooltip: t(disabledTooltipKey) } as const)
              : {};

            return (
              <ButtonDropdown.Item
                as="button"
                key={wordList.id}
                value={wordList.id}
                text={wordList.name}
                textIcon={<MaterialWordListIcon data-testid={`word-list-icon-${wordList.id}`} />}
                onClick={() => openWordlist(wordList)}
                {...disabledProps}
              />
            );
          })}
        </DropdownMenuGroup>
      )}
      {otherApps.length > 0 && (
        <DropdownMenuGroup title={t('PAGES.TOC_LEVEL.APPS.MOBILE.OTHER_APPS_GROUP')}>
          {otherApps.map(app => {
            const isPreview = app.state === 'preview';
            const disabledProps = isPreview
              ? ({ disabled: true, tooltip: t('PAGES.TOC_LEVEL.APPS.QUICK_ACTION.PREVIEW_TOOLTIP') } as const)
              : {};

            return (
              <ButtonDropdown.Item
                key={app.id}
                value={app.linkedApp?.id}
                text={app.name}
                textIcon={<MaterialSiteIcon data-testid={`app-quick-action-icon-${app.id}`} />}
                {...disabledProps}
                {...getAppActionProps(app)}
              />
            );
          })}
        </DropdownMenuGroup>
      )}
    </ButtonDropdown>
  );
}
